import { useEffect, useRef } from "react";

const CANDLE_UP = "#10B981";
const CANDLE_DOWN = "#EF4444";
const LINE_UP = "#059669";
const LINE_DOWN = "#E11D48";

const TF_CONFIG = {
    "5M": { seed: 77, vol: 0.7, drift: 0.65, count: 156 },
};

function mulberry32(a) {
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const REGIMES = [
    { drift: 0.62, vol: 0.9, min: 26, max: 38 },
    { drift: -0.66, vol: 1.05, min: 22, max: 34 },
    { drift: 0.08, vol: 0.75, min: 14, max: 22 },
    { drift: 0.8, vol: 0.85, min: 20, max: 30 },
    { drift: -0.5, vol: 0.95, min: 18, max: 28 },
    { drift: 0.35, vol: 0.7, min: 16, max: 26 },
];

function genSeries(seed, count, volScale, driftScale) {
    const rnd = mulberry32(seed);
    const candles = [];
    let price = 412 + rnd() * 8;
    let ri = Math.floor(rnd() * REGIMES.length);
    while (candles.length < count) {
        const reg = REGIMES[ri % REGIMES.length];
        const len = Math.round(reg.min + rnd() * (reg.max - reg.min));
        for (let k = 0; k < len && candles.length < count; k++) {
            const o = price;
            const c = o + reg.drift * driftScale + (rnd() - 0.5) * 2 * reg.vol * volScale;
            const h = Math.max(o, c) + rnd() * reg.vol * volScale * 0.7;
            const l = Math.min(o, c) - rnd() * reg.vol * volScale * 0.7;
            const v = 0.25 + Math.abs(c - o) * 0.7 + rnd() * 0.55;
            candles.push({ o, h, l, c, v });
            price = c;
        }
        ri++;
    }
    return candles;
}

function computeFlow(candles, period = 10, mult = 2.1) {
    const n = candles.length;
    const line = new Array(n).fill(null);
    const trend = new Array(n).fill(1);
    const trs = new Array(n).fill(0);
    for (let i = 1; i < n; i++) {
        const { h, l } = candles[i];
        const pc = candles[i - 1].c;
        trs[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
    }
    let prevUpper = Infinity,
        prevLower = 0,
        prevTrend = 1;
    for (let i = period; i < n; i++) {
        let atr = 0;
        for (let j = i - period + 1; j <= i; j++) atr += trs[j];
        atr /= period;
        const mid = (candles[i].h + candles[i].l) / 2;
        const basicUpper = mid + mult * atr;
        const basicLower = mid - mult * atr;
        const pc = candles[i - 1].c;
        const finalUpper = basicUpper < prevUpper || pc > prevUpper ? basicUpper : prevUpper;
        const finalLower = basicLower > prevLower || pc < prevLower ? basicLower : prevLower;
        const t = prevTrend === 1 ? (candles[i].c < finalLower ? -1 : 1) : candles[i].c > finalUpper ? 1 : -1;
        trend[i] = t;
        line[i] = t === 1 ? finalLower : finalUpper;
        prevTrend = t;
        prevUpper = finalUpper;
        prevLower = finalLower;
    }
    return { line, trend, period };
}

export default function ItfChart({ timeframe = "5M", height = 400, startDelay = 300, className = "" }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        const cfg = TF_CONFIG[timeframe] || TF_CONFIG["5M"];
        let raf = 0;
        let disposed = false;
        let seed = cfg.seed;
        let candles = [];
        let flow = null;
        let progress = 0;
        let phase = "wait";
        let phaseStart = performance.now();
        let fade = 1;
        let W = 600;
        const H = height;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        const resize = () => {
            const w = wrapRef.current?.clientWidth || 600;
            W = w;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(H * dpr);
            canvas.style.width = w + "px";
            canvas.style.height = H + "px";
        };
        const ro = new ResizeObserver(resize);
        ro.observe(wrapRef.current);
        resize();

        const regen = () => {
            candles = genSeries(seed, cfg.count, cfg.vol, cfg.drift);
            flow = computeFlow(candles);
            progress = 0;
            phase = "draw";
            fade = 1;
            phaseStart = performance.now();
        };

        const rr = (x, y, w, h, r) => {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + w, y, x + w, y + h, r);
            ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r);
            ctx.arcTo(x, y, x + w, y, r);
            ctx.closePath();
        };

        const draw = (now) => {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, W, H);
            if (phase === "wait" || !candles.length) return;

            const N = cfg.count;
            const L = 6,
                R = 62,
                T = 18,
                B = 8;
            const innerH = H - T - B;
            const volH = Math.max(26, innerH * 0.13);
            const priceH = innerH - volH - 12;
            const count = Math.max(2, Math.min(N, Math.floor(progress)));
            const vis = candles.slice(0, count);

            if (phase === "hold" || phase === "fade") {
                const last = vis[vis.length - 1];
                const jitter = Math.sin(now / 260) * 0.2 * cfg.vol + Math.sin(now / 97) * 0.09 * cfg.vol;
                const nc = last.c + jitter;
                vis[vis.length - 1] = { ...last, c: nc, h: Math.max(last.h, nc), l: Math.min(last.l, nc) };
            }

            let minP = Infinity,
                maxP = -Infinity,
                maxV = 0.001;
            for (const c of vis) {
                minP = Math.min(minP, c.l);
                maxP = Math.max(maxP, c.h);
                maxV = Math.max(maxV, c.v);
            }
            for (let i = 0; i < count; i++) {
                const lv = flow.line[i];
                if (lv != null) {
                    minP = Math.min(minP, lv);
                    maxP = Math.max(maxP, lv);
                }
            }
            const pad = (maxP - minP) * 0.12 || 1;
            minP -= pad;
            maxP += pad;

            const step = (W - L - R) / N;
            const x = (i) => L + i * step + step / 2;
            const y = (p) => T + ((maxP - p) / (maxP - minP)) * priceH;

            ctx.globalAlpha = Math.max(0, fade);

            ctx.strokeStyle = "rgba(148,163,184,0.18)";
            ctx.lineWidth = 1;
            ctx.fillStyle = "#94A3B8";
            ctx.font = "10px 'JetBrains Mono', monospace";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            for (let g = 0; g <= 4; g++) {
                const p = minP + ((maxP - minP) * g) / 4;
                const gy = Math.round(y(p)) + 0.5;
                ctx.beginPath();
                ctx.moveTo(L, gy);
                ctx.lineTo(W - R + 8, gy);
                ctx.stroke();
                ctx.fillText(p.toFixed(2), W - R + 12, gy);
            }

            const volBase = T + priceH + 12 + volH;
            vis.forEach((c, i) => {
                const bh = Math.max(2, (c.v / maxV) * volH);
                ctx.fillStyle = c.c >= c.o ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)";
                ctx.fillRect(x(i) - step * 0.28, volBase - bh, Math.max(1.5, step * 0.56), bh);
            });

            const bw = Math.max(2.5, step * 0.56);
            vis.forEach((c, i) => {
                const up = c.c >= c.o;
                const col = up ? CANDLE_UP : CANDLE_DOWN;
                ctx.strokeStyle = col;
                ctx.fillStyle = col;
                ctx.lineWidth = Math.max(1, step * 0.09);
                const cx = x(i);
                ctx.beginPath();
                ctx.moveTo(cx, y(c.h));
                ctx.lineTo(cx, y(c.l));
                ctx.stroke();
                const yo = y(c.o);
                const yc = y(c.c);
                const top = Math.min(yo, yc);
                const hgt = Math.max(1.5, Math.abs(yo - yc));
                ctx.fillRect(cx - bw / 2, top, bw, hgt);
            });

            // ITF trend line — green below price when bullish, red above price when bearish
            ctx.lineWidth = 2.5;
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            for (let i = flow.period + 1; i < count; i++) {
                const a = flow.line[i - 1];
                const b = flow.line[i];
                if (a == null || b == null) continue;
                const col = flow.trend[i] === 1 ? LINE_UP : LINE_DOWN;
                ctx.strokeStyle = col;
                ctx.shadowColor = col;
                ctx.shadowBlur = 6;
                ctx.beginPath();
                ctx.moveTo(x(i - 1), y(a));
                ctx.lineTo(x(i), y(b));
                ctx.stroke();
            }
            ctx.shadowBlur = 0;

            if (phase === "draw" && count > 2) {
                const i = count - 1;
                const cx = x(i);
                ctx.strokeStyle = "rgba(15,23,42,0.16)";
                ctx.setLineDash([3, 4]);
                ctx.beginPath();
                ctx.moveTo(cx, T);
                ctx.lineTo(cx, volBase);
                ctx.stroke();
                ctx.setLineDash([]);
                const col = flow.trend[i] === 1 ? LINE_UP : LINE_DOWN;
                ctx.fillStyle = col;
                ctx.shadowColor = col;
                ctx.shadowBlur = 12;
                ctx.beginPath();
                ctx.arc(cx, y(vis[i].c), 3.5, 0, Math.PI * 2);
                ctx.fill();
                ctx.shadowBlur = 0;
            }

            const lastPrice = vis[vis.length - 1].c;
            const py = Math.min(Math.max(y(lastPrice), T + 9), T + priceH - 9);
            const label = lastPrice.toFixed(2);
            ctx.font = "600 10px 'JetBrains Mono', monospace";
            const tw = ctx.measureText(label).width;
            ctx.fillStyle = "#0F172A";
            rr(W - R + 10, py - 9, tw + 14, 18, 6);
            ctx.fill();
            ctx.fillStyle = "#FFFFFF";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(label, W - R + 17, py + 0.5);

            ctx.globalAlpha = 1;
        };

        const tick = (now) => {
            if (disposed) return;
            if (phase === "wait") {
                if (now - phaseStart > startDelay) {
                    phase = "draw";
                    phaseStart = now;
                }
            } else if (phase === "draw") {
                progress += Math.max(0.35, cfg.count / 300);
                if (progress >= cfg.count) {
                    progress = cfg.count;
                    phase = "hold";
                    phaseStart = now;
                }
            } else if (phase === "hold") {
                if (now - phaseStart > 2600) {
                    phase = "fade";
                    phaseStart = now;
                }
            } else if (phase === "fade") {
                fade = 1 - (now - phaseStart) / 650;
                if (fade <= 0) {
                    seed += 97;
                    regen();
                }
            }
            draw(now);
            raf = requestAnimationFrame(tick);
        };

        candles = genSeries(seed, cfg.count, cfg.vol, cfg.drift);
        flow = computeFlow(candles);
        raf = requestAnimationFrame(tick);
        return () => {
            disposed = true;
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [timeframe, height, startDelay]);

    return (
        <div ref={wrapRef} className={`w-full ${className}`} style={{ height }} data-testid="itf-chart-canvas">
            <canvas ref={canvasRef} className="block" />
        </div>
    );
}
